import type { HomeFeedCard } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useRouter } from 'expo-router'
import { useCallback, useState } from 'react'
import { View } from 'react-native'
import Animated, { FadeIn, ZoomOutLeft } from 'react-native-reanimated'
import { HomeFeedCardComponent } from '~/components/feed/HomeFeedCard'
import Icon from '~/components/Icon'
import { Heading2 } from '~/components/typography/Titles'
import { Text } from '~/components/ui/text'
import type { UseHomeFeedStackResult } from '~/data/feed/useHomeFeedStack'
import { cn } from '~/lib/utils'
import ContentBox from '../layout/ContentBox'
import { LoadingView } from '../LoadingView'

function FeedStackEmpty() {
  const { t } = useTranslations('feed')

  return (
    <ContentBox
      scrollable={false}
      className="items-center justify-center gap-3 px-4 py-4"
      contentContainerClassName="items-center gap-4">
      <View className="size-16 items-center justify-center self-center rounded-full bg-muted p-2">
        <Icon name="checkmark-circle-outline" size={28} className="text-secondary" />
      </View>
      <Heading2 className="text-center font-semibold text-lg text-primary">
        {t(($) => $.stack.empty.title)}
      </Heading2>
      <Text className="text-center text-base/relaxed text-secondary">
        {t(($) => $.stack.empty.description)}
      </Text>
    </ContentBox>
  )
}

function FeedStackHeader({ remainingCount }: { remainingCount: number }) {
  const { t } = useTranslations('feed')

  if (remainingCount === 0) {
    return null
  }

  return (
    <View className="mx-2 -mb-8 flex-row items-center justify-between rounded-t-content bg-card px-6 py-6 pb-12">
      <Text className="font-normal text-lg/normal text-primary">{t(($) => $.stack.title)}</Text>
      <View className="items-center justify-center rounded-full bg-accent px-2.5 py-1">
        <Text className="font-semibold text-xxs/none text-accent-foreground">{remainingCount}</Text>
      </View>
    </View>
  )
}

function DeckBackground({ count }: { count: number }) {
  if (count < 2) {
    return null
  }

  return (
    <>
      {count >= 3 && (
        <View
          className="absolute bottom-0 left-4 right-4 h-full rounded-content-inner bg-primary/35"
          style={{ transform: [{ translateY: 10 }, { scaleX: 0.88 }] }}
        />
      )}
      <View
        className="absolute bottom-0 left-2 right-2 h-full rounded-content-inner bg-primary/50"
        style={{ transform: [{ translateY: 5 }, { scaleX: 0.94 }] }}
      />
    </>
  )
}

export function HomeFeedStack({ stack }: { stack: UseHomeFeedStackResult }) {
  const router = useRouter()
  const {
    currentCard,
    currentCardIndex,
    dismissCard: dismissStackCard,
    hasPagedThroughStack,
    isAdvancing,
    isLoading,
    remainingCount,
    totalCount,
  } = stack

  const [minHeight, setMinHeight] = useState(0)

  const handleOpen = useCallback(
    (card: HomeFeedCard) => {
      dismissStackCard(card)

      if (card.kind === 'asset_match' || card.kind === 'asset_added') {
        router.push({
          pathname: '/property/[assetId]',
          params: { assetId: card.asset.id },
        })
      } else {
        router.push({
          pathname: '/leads/[leadId]',
          params: { leadId: card.lead.id },
        })
      }
    },
    [dismissStackCard, router]
  )

  const handleNext = useCallback(
    (card: HomeFeedCard) => {
      dismissStackCard(card)
    },
    [dismissStackCard]
  )

  if (isLoading && !currentCard && remainingCount === 0) {
    return <LoadingView />
  }

  if (remainingCount === 0 && !hasPagedThroughStack) {
    return null
  }

  const contentKey = currentCard
    ? currentCard.id
    : isAdvancing
      ? `loading-${currentCardIndex}-${remainingCount}`
      : `empty-${hasPagedThroughStack ? 'seen' : 'hidden'}`

  return (
    <View>
      <FeedStackHeader remainingCount={remainingCount} />
      <Animated.View className="relative">
        <DeckBackground count={remainingCount} />
        <Animated.View
          style={{ minHeight: remainingCount > 0 ? minHeight : undefined }}
          className={cn(
            'rounded-content bg-transparent transition-all duration-150 ease-out',
            remainingCount > 0 && 'bg-secondary'
          )}>
          {currentCard ? (
            <Animated.View
              key={contentKey}
              entering={FadeIn.duration(150)}
              exiting={ZoomOutLeft.springify().damping(25).stiffness(450)}
              onLayout={(event) => currentCard && setMinHeight(event.nativeEvent.layout.height)}>
              <HomeFeedCardComponent
                card={currentCard}
                cardIndex={currentCardIndex}
                totalCards={totalCount}
                onOpen={handleOpen}
                onNext={handleNext}
              />
            </Animated.View>
          ) : remainingCount === 0 ? (
            <FeedStackEmpty />
          ) : null}
        </Animated.View>
      </Animated.View>
    </View>
  )
}
